import React, { useEffect, useState } from "react";
import { BeatLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import "./slider_admin.css";

const INITIAL_NEW_SLIDE = {
  title: "",
  imageUrl: "",
  imageFile: null,
  isActive: true,
};

const normalizeSlide = (slide, index) => ({
  id: slide.id,
  title: slide.title || "",
  image_url: slide.image_url || "",
  display_order: Number(slide.display_order) || index + 1,
  is_active: Boolean(slide.is_active),
  pendingImageUrl: "",
  pendingImageFile: null,
});

export function SliderAdmin() {
  const url = process.env.REACT_APP_API_URL || "";
  const navigate = useNavigate();
  const isAdmin = useSelector((state) => state.permission.isAdmin);

  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingOrder, setSavingOrder] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [newSlide, setNewSlide] = useState(INITIAL_NEW_SLIDE);

  const fetchSlides = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetch(`${url}/api/admin/slider-slides`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Nem sikerült lekérdezni a slider képeket.");
      }
      const data = await response.json();
      const normalized = Array.isArray(data)
        ? data
            .sort((a, b) => Number(a.display_order) - Number(b.display_order))
            .map(normalizeSlide)
        : [];
      setSlides(normalized);
    } catch (err) {
      setError(err.message || "Ismeretlen hiba történt.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moveSlide = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= slides.length) return;

    const nextSlides = [...slides];
    const tmp = nextSlides[index];
    nextSlides[index] = nextSlides[nextIndex];
    nextSlides[nextIndex] = tmp;

    setSlides(
      nextSlides.map((slide, idx) => ({
        ...slide,
        display_order: idx + 1,
      }))
    );
  };

  const handleSaveOrder = async () => {
    try {
      setSavingOrder(true);
      const payload = {
        items: slides.map((slide, index) => ({
          id: slide.id,
          display_order: index + 1,
        })),
      };
      const response = await fetch(`${url}/api/admin/slider-slides/reorder`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Nem sikerült menteni a sorrendet.");
      }
      await fetchSlides();
    } catch (err) {
      alert(err.message || "Hiba a sorrend mentésekor.");
    } finally {
      setSavingOrder(false);
    }
  };

  const handleSlideField = (slideId, field, value) => {
    setSlides((prev) =>
      prev.map((slide) => (slide.id === slideId ? { ...slide, [field]: value } : slide))
    );
  };

  const handleUpdateSlide = async (slide) => {
    try {
      const formData = new FormData();
      formData.append("title", slide.title || "");
      formData.append("displayOrder", String(slide.display_order));
      formData.append("isActive", String(slide.is_active));
      if (slide.pendingImageFile) {
        formData.append("imageFile", slide.pendingImageFile);
      } else if (slide.pendingImageUrl.trim()) {
        formData.append("imageUrl", slide.pendingImageUrl.trim());
      }

      const response = await fetch(`${url}/api/admin/slider-slides/${slide.id}`, {
        method: "PUT",
        credentials: "include",
        body: formData,
      });
      if (!response.ok) {
        throw new Error("Nem sikerült frissíteni a slider képet.");
      }
      await fetchSlides();
    } catch (err) {
      alert(err.message || "Hiba a slider kép frissítésekor.");
    }
  };

  const handleDeleteSlide = async (slideId) => {
    if (!window.confirm("Biztosan törlöd ezt a slider képet?")) return;
    try {
      const response = await fetch(`${url}/api/admin/slider-slides/${slideId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Nem sikerult törölni a slider képet.");
      }
      setSlides((prev) =>
        prev
          .filter((slide) => slide.id !== slideId)
          .map((slide, index) => ({ ...slide, display_order: index + 1 }))
      );
    } catch (err) {
      alert(err.message || "Hiba a törlés során.");
    }
  };

  const handleCreateSlide = async (e) => {
    e.preventDefault();
    if (!newSlide.imageFile && !newSlide.imageUrl.trim()) {
      alert("Adj meg képfájlt vagy kép URL-t.");
      return;
    }

    try {
      setCreating(true);
      const formData = new FormData();
      formData.append("title", newSlide.title.trim());
      formData.append("displayOrder", String(slides.length + 1));
      formData.append("isActive", String(newSlide.isActive));
      if (newSlide.imageFile) {
        formData.append("imageFile", newSlide.imageFile);
      } else {
        formData.append("imageUrl", newSlide.imageUrl.trim());
      }

      const response = await fetch(`${url}/api/admin/slider-slides`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!response.ok) {
        throw new Error("Nem sikerült létrehozni a slider képet.");
      }
      setNewSlide(INITIAL_NEW_SLIDE);
      await fetchSlides();
    } catch (err) {
      alert(err.message || "Hiba a slider létrehozásakor.");
    } finally {
      setCreating(false);
    }
  };

  if (!isAdmin) {
    return <div className="error">Hozzáférés megtagadva. Csak adminisztrátoroknak.</div>;
  }

  if (loading) {
    return (
      <div className="slider-admin loader-container">
        <BeatLoader color="#001662" size={24} loading={loading} />
      </div>
    );
  }

  return (
    <div className="slider-admin">
      <div className="slider-admin__head">
        <h1>Kezdőlapi slider kezelés</h1>
        <button type="button" onClick={() => navigate("/admin")}>
          Vissza az adminhoz
        </button>
      </div>

      {error && <div className="slider-admin__error">Hiba: {error}</div>}

      <form className="slider-admin__create" onSubmit={handleCreateSlide}>
        <h2>Új slider kép</h2>
        <input
          type="text"
          placeholder="Cím (opcionális)"
          value={newSlide.title}
          onChange={(e) => setNewSlide((prev) => ({ ...prev, title: e.target.value }))}
        />
        <input
          type="url"
          placeholder="Vagy kép URL"
          value={newSlide.imageUrl}
          onChange={(e) => setNewSlide((prev) => ({ ...prev, imageUrl: e.target.value }))}
        />
        <input
          type="file"
          accept="image/*"
          onChange={(e) =>
            setNewSlide((prev) => ({
              ...prev,
              imageFile: e.target.files && e.target.files[0] ? e.target.files[0] : null,
            }))
          }
        />
        <label className="slider-admin__checkbox">
          <input
            type="checkbox"
            checked={newSlide.isActive}
            onChange={(e) => setNewSlide((prev) => ({ ...prev, isActive: e.target.checked }))}
          />
          Aktív
        </label>
        <button type="submit" disabled={creating}>
          {creating ? "Mentése folyamatban..." : "Kép hozzáadása"}
        </button>
      </form>

      <div className="slider-admin__order-bar">
        <h2>Meglévő slider képek</h2>
        <button type="button" onClick={handleSaveOrder} disabled={savingOrder || !slides.length}>
          {savingOrder ? "Sorrend mentése..." : "Sorrend mentése"}
        </button>
      </div>

      <div className="slider-admin__list">
        {slides.map((slide, index) => (
          <article key={slide.id} className="slider-admin__card">
            <div className="slider-admin__image-wrap">
              {slide.image_url ? (
                <img src={slide.image_url} alt={slide.title || `Slider kép ${slide.id}`} />
              ) : (
                <div className="slider-admin__image-empty">Nincs kép</div>
              )}
            </div>

            <div className="slider-admin__content">
              <div className="slider-admin__order-controls">
                <span>Sorrend: {index + 1}</span>
                <div>
                  <button type="button" onClick={() => moveSlide(index, -1)} disabled={index === 0}>
                    Fel
                  </button>
                  <button
                    type="button"
                    onClick={() => moveSlide(index, 1)}
                    disabled={index === slides.length - 1}
                  >
                    Le
                  </button>
                </div>
              </div>

              <input
                type="text"
                value={slide.title}
                onChange={(e) => handleSlideField(slide.id, "title", e.target.value)}
                placeholder="Cím"
              />

              <input
                type="url"
                value={slide.pendingImageUrl}
                onChange={(e) => handleSlideField(slide.id, "pendingImageUrl", e.target.value)}
                placeholder="Új kép URL (opcionális)"
              />

              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleSlideField(
                    slide.id,
                    "pendingImageFile",
                    e.target.files && e.target.files[0] ? e.target.files[0] : null
                  )
                }
              />

              <label className="slider-admin__checkbox">
                <input
                  type="checkbox"
                  checked={slide.is_active}
                  onChange={(e) => handleSlideField(slide.id, "is_active", e.target.checked)}
                />
                Aktív
              </label>

              <div className="slider-admin__actions">
                <button type="button" onClick={() => handleUpdateSlide(slide)}>
                  Mentés
                </button>
                <button
                  type="button"
                  className="danger"
                  onClick={() => handleDeleteSlide(slide.id)}
                >
                  Törlés
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
